"use client";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { WebShopIcon } from "@/src/components/WebShopIcon";
import { Button } from "@/src/components/Button";
import { ApiCallsAutoSearchSuccessModalData } from "../../types";
import Image from "next/image";
import { safeParseJson } from "@/src/shared/utils/form";
import { useCallback, useState } from "react";
import { useUpdateField } from "@/src/hooks/blockly/useHandleBlocklyFields";
import { useFormContext } from "react-hook-form";
import { useContextStore } from "@/src/stores/useContextStore";
import { DataTypeEnum } from "@/src/shared/models/contextInstance";
import { maskFunctions } from "@/src/shared/utils/mask";

export const useFieldsReset = () => {
  const { getVariable } = useContextStore();
  const { updateFieldAsync } = useUpdateField();
  const { setValue } = useFormContext();

  // const getDefaultValue = () => {
  //   const variable = getVariable(key);
  //   return null;
  // };

  const resetFields = useCallback(
    (fields: string[]) => {
      const promises = fields.map((field) => {
        const varType = getVariable(field)?.dataType;
        // updateVariableValue(field, null);

        const newValue = varType === DataTypeEnum.BOOLEAN ? false : null;
        setValue(field, newValue, { shouldDirty: true });
        return updateFieldAsync(field, newValue, true);
      });

      return Promise.allSettled(promises);
    },
    [updateFieldAsync, setValue],
  );

  return resetFields;
};

export const mtsbumvsFields = [
  "plateNumber",
  "vin",
  "brand",
  "model",
  "vehicleType",
  "vehicleCategory",
  "constructionYear",
  "taxiUsage",
  "requiredTO",
  "markId",
  "modelId",
  "engineCapacity",
  "engineType",
  "batteryCapacity",
  "ownWeight",
  "totalWeight",
  "placesCount",
];

const toKebabCase = (str: string) => {
  return str
    ?.toLowerCase()
    .replace(/ /g, "-")
    .replace(/\./g, "")
    .replace(/_/g, "-");
};

export const ApiCallsAutoSearchSuccessModal = ({
  onClose,
  onSubmit,
  plateNumber,
  model,
  brand,
}: ApiCallsAutoSearchSuccessModalData & {
  onClose: () => void;
  onSubmit: () => void;
}) => {
  const brandValue = safeParseJson(brand)?.value;
  const modelValue = safeParseJson(model)?.value;
  const resetFields = useFieldsReset();

  const [logoSrc, setLogoSrc] = useState(
    `/vehicle-logos/${toKebabCase(brandValue)}.png`,
  );

  const handleClose = async () => {
    await resetFields(mtsbumvsFields);
    // setTimeout(onClose, 5000);
    onClose();
  };

  return (
    <Box className="flex w-full max-w-[524px] flex-col gap-6 rounded-xl bg-white p-6 shadow-xl center-modal max-md:animate-up-modal max-md:bottom-modal">
      <Stack className="w-full flex-center-row">
        <Typography
          variant="h1"
          component="h3"
          className="flex-1 text-center text-[25px] font-semibold leading-8"
        >
          {"Транспортний засіб"}
        </Typography>
        <IconButton onClick={handleClose}>
          <WebShopIcon name="close" className="fill-blue" />
        </IconButton>
      </Stack>
      <Stack className="flex flex-col gap-2 rounded-lg bg-super-light-blue text-base text-dark-gray">
        <Box className="flex flex-col items-center justify-center gap-3 p-6 pl-4">
          {brandValue && (
            <Box className="relative flex aspect-square w-[68px] items-center justify-center overflow-hidden rounded-full bg-white p-2">
              <Image
                src={logoSrc}
                alt={brandValue}
                width={68}
                height={68}
                quality={60}
                objectFit={"contain"}
                onError={() => setLogoSrc("/vehicle-logos/default-car.png")}
              />
            </Box>
          )}
          <Box className={"flex flex-col items-center gap-1"}>
            <Box className="text-center text-lg font-semibold text-dark-blue">
              {brandValue} {modelValue}
            </Box>
            <Box className={"flex items-center justify-center gap-2"}>
              <Box className="flex h-[24px] w-[27px] rounded-l">
                <WebShopIcon name="uaFlag" />
              </Box>
              <Box className="text-lg text-dark-blue">
                {maskFunctions.numberPlate(plateNumber || "")}
              </Box>
            </Box>
          </Box>
        </Box>
      </Stack>
      <Stack className="flex flex-row flex-nowrap gap-3 [&>*]:flex-1">
        <Button variant={"outlined"} onClick={handleClose}>
          це не моє авто
        </Button>
        <Button onClick={onSubmit}>Продовжити</Button>
      </Stack>
    </Box>
  );
};
